import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "../../../hooks/useTranslation";
import { StateType } from "../../../backend/store";
import { hasPermission } from "../../../utils/permissions";
import { ONBOARDING_ROOT } from "../../../backend/sagas/onboardingRedirect";
import { useFrameMode } from "../../../next/shell/frameMode";
import { useNewFrameShown } from "../../../next/shell/useNewFrameShown";
import { useNextFrameMounted } from "../../../next/shell/frameMounted";

const NUDGE_DISMISSED_KEY = "countryNudgeDismissed";

const CountryNudge: React.FC = () => {
  const { p } = useTranslation("countryNudge");
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const currentUser = useSelector((state: StateType) => state.authStore.currentUser);
  const workspace = useSelector((state: StateType) => state.authStore.workspace);

  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(NUDGE_DISMISSED_KEY) === "true"
  );

  const canEdit = hasPermission("update_settings", currentUser);
  // Walkthrough public #11: today's floating bar belongs to today's frame; in the new frame it covered page content.
  const frame = useFrameMode();

  // Never inside registration. It is mounted app-wide, so it floated over the
  // signup's own map, and "Set now" goes to /settings — out of the flow the
  // person is in the middle of. Registration is where the location gets set in
  // the first place; the nudge is for someone who has already left it without.
  const inSignupFlow = pathname.startsWith(ONBOARDING_ROOT);
  // Not on the staff pages (their own frame; no account map there), and in the new frame never over the menu column's account card
  // (walkthrough 2026-10-29, issue 27).
  const onStaffPages = /^\/(next\/)?admin(\/|$)/.test(pathname);
  const inNextFrame = useNextFrameMounted();
  const newFrameShown = useNewFrameShown();

  const shouldShow =
    !!currentUser &&
    !!workspace &&
    workspace.map_center_lat == null &&
    canEdit &&
    !inSignupFlow &&
    frame !== "next" &&
    !newFrameShown &&
    !onStaffPages &&
    !dismissed;

  const handleDismiss = () => {
    sessionStorage.setItem(NUDGE_DISMISSED_KEY, "true");
    setDismissed(true);
  };

  if (!shouldShow) return null;

  return (
    <Alert
      severity="info"
      sx={{
        position: "fixed",
        bottom: 24,
        left: inNextFrame ? { xs: 16, md: 280 } : 24,
        zIndex: 1200,
        maxWidth: 480,
        boxShadow: 3,
        borderRadius: 1,
      }}
      action={
        <>
          <Button
            size="small"
            color="inherit"
            onClick={() => navigate("/settings")}
            sx={{ mr: 1 }}
          >
            {p("set_now")}
          </Button>
          <IconButton
            size="small"
            color="inherit"
            onClick={handleDismiss}
            aria-label={p("dismiss")}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      }
    >
      {p("message")}
    </Alert>
  );
};

export default CountryNudge;
